/**
 * Resource Components
 * Manages resources and categories for student learning materials
 */

import React, { useState, useEffect } from 'react';
import { WordOfDayDisplay, QuoteOfDayDisplay } from './WordOfDayComponents';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

/**
 * Helper function to ensure URL has a protocol
 */
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  const trimmedUrl = url.trim();
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
};

/**
 * Custom hook for managing Resources state and handlers
 */
export const useResourceManagement = ({ currentUser }) => {
  const [resources, setResources] = useState([]);
  const [resourceCategories, setResourceCategories] = useState([]);
  
  // Resource form state
  const [newResource, setNewResource] = useState({ title: '', url: '', description: '', categoryId: '' });
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [editResourceForm, setEditResourceForm] = useState({ title: '', url: '', description: '', categoryId: '' });
  
  // Category form state
  const [newCategory, setNewCategory] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  // Listen for Resources
  useEffect(() => {
    if (!currentUser) return;

    const qResources = query(collection(db, 'resources'), orderBy('order', 'asc'));
    const unsubscribeResources = onSnapshot(qResources, (snapshot) => {
      const resourcesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setResources(resourcesData);
    }, (error) => console.error("Error fetching resources:", error));

    return () => unsubscribeResources();
  }, [currentUser]);

  // Listen for Resource Categories
  useEffect(() => {
    if (!currentUser) return;

    const qResourceCategories = query(collection(db, 'resourceCategories'), orderBy('order', 'asc'));
    const unsubscribeResourceCategories = onSnapshot(qResourceCategories, (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setResourceCategories(categoriesData);
    }, (error) => console.error("Error fetching resource categories:", error));

    return () => unsubscribeResourceCategories();
  }, [currentUser]);

  // Category management handlers
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      alert('Please provide a category name.');
      return;
    }

    try {
      const maxOrder = resourceCategories.length > 0 ? Math.max(...resourceCategories.map(c => c.order || 0)) : 0;
      await addDoc(collection(db, 'resourceCategories'), {
        name: newCategory.trim(),
        order: maxOrder + 1,
        createdAt: new Date().toISOString()
      });
      setNewCategory('');
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category. Please try again.');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditCategoryName(category.name);
  };

  const handleSaveCategory = async () => {
    if (!editCategoryName.trim()) {
      alert('Please provide a category name.');
      return;
    }

    try {
      const categoryRef = doc(db, 'resourceCategories', editingCategoryId);
      await updateDoc(categoryRef, {
        name: editCategoryName.trim()
      });
      setEditingCategoryId(null);
      setEditCategoryName('');
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category. Please try again.');
    }
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditCategoryName('');
  };

  const handleDeleteCategory = async (categoryId) => {
    // Check if any resources are in this category
    const resourcesInCategory = resources.filter(r => r.categoryId === categoryId);
    if (resourcesInCategory.length > 0) {
      alert(`Cannot delete category. Please reassign or delete the ${resourcesInCategory.length} resource(s) in this category first.`);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'resourceCategories', categoryId));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category. Please try again.');
    }
  };

  const handleMoveCategoryUp = async (category) => {
    const currentIndex = resourceCategories.findIndex(c => c.id === category.id);
    if (currentIndex === -1 || currentIndex === 0) return;

    const previousCategory = resourceCategories[currentIndex - 1];
    
    try {
      await updateDoc(doc(db, 'resourceCategories', category.id), { order: previousCategory.order });
      await updateDoc(doc(db, 'resourceCategories', previousCategory.id), { order: category.order });
    } catch (error) {
      console.error('Error reordering category:', error);
      alert('Failed to reorder category. Please try again.');
    }
  };

  const handleMoveCategoryDown = async (category) => {
    const currentIndex = resourceCategories.findIndex(c => c.id === category.id);
    if (currentIndex === -1 || currentIndex >= resourceCategories.length - 1) return;

    const nextCategory = resourceCategories[currentIndex + 1];
    
    try {
      await updateDoc(doc(db, 'resourceCategories', category.id), { order: nextCategory.order });
      await updateDoc(doc(db, 'resourceCategories', nextCategory.id), { order: category.order });
    } catch (error) {
      console.error('Error reordering category:', error);
      alert('Failed to reorder category. Please try again.');
    }
  };

  // Resource management handlers
  const handleAddResource = async () => {
    if (!newResource.title.trim() || !newResource.url.trim()) {
      alert('Please provide both a title and URL.');
      return;
    }

    try {
      const categoryId = newResource.categoryId || '';
      const resourcesInCategory = resources.filter(r => (r.categoryId || '') === categoryId);
      const maxOrder = resourcesInCategory.length > 0 ? Math.max(...resourcesInCategory.map(r => r.order || 0)) : 0;
      
      await addDoc(collection(db, 'resources'), {
        title: newResource.title.trim(),
        url: newResource.url.trim(),
        description: newResource.description.trim(),
        categoryId: categoryId,
        order: maxOrder + 1,
        createdAt: new Date().toISOString()
      });
      
      setNewResource({ title: '', url: '', description: '', categoryId: '' });
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('Failed to add resource. Please try again.');
    }
  };

  const handleEditResource = (resource) => {
    setEditingResourceId(resource.id);
    setEditResourceForm({
      title: resource.title,
      url: resource.url,
      description: resource.description || '',
      categoryId: resource.categoryId || ''
    });
  };

  const handleSaveResource = async () => {
    if (!editResourceForm.title.trim() || !editResourceForm.url.trim()) {
      alert('Please provide both a title and URL.');
      return;
    }

    try {
      const resourceRef = doc(db, 'resources', editingResourceId);
      await updateDoc(resourceRef, {
        title: editResourceForm.title.trim(),
        url: editResourceForm.url.trim(),
        description: editResourceForm.description.trim(),
        categoryId: editResourceForm.categoryId || ''
      });
      setEditingResourceId(null);
      setEditResourceForm({ title: '', url: '', description: '', categoryId: '' });
    } catch (error) {
      console.error('Error updating resource:', error);
      alert('Failed to update resource. Please try again.');
    }
  };

  const handleCancelEditResource = () => {
    setEditingResourceId(null);
    setEditResourceForm({ title: '', url: '', description: '', categoryId: '' });
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'resources', resourceId));
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource. Please try again.');
    }
  };

  const handleMoveResourceUp = async (resource) => {
    const categoryId = resource.categoryId || '';
    const resourcesInCategory = resources.filter(r => (r.categoryId || '') === categoryId).sort((a, b) => a.order - b.order);
    const currentIndex = resourcesInCategory.findIndex(r => r.id === resource.id);
    if (currentIndex === -1 || currentIndex === 0) return;

    const previousResource = resourcesInCategory[currentIndex - 1];
    
    try {
      await updateDoc(doc(db, 'resources', resource.id), { order: previousResource.order });
      await updateDoc(doc(db, 'resources', previousResource.id), { order: resource.order });
    } catch (error) {
      console.error('Error reordering resource:', error);
      alert('Failed to reorder resource. Please try again.');
    }
  };

  const handleMoveResourceDown = async (resource) => {
    const categoryId = resource.categoryId || '';
    const resourcesInCategory = resources.filter(r => (r.categoryId || '') === categoryId).sort((a, b) => a.order - b.order);
    const currentIndex = resourcesInCategory.findIndex(r => r.id === resource.id);
    if (currentIndex === -1 || currentIndex >= resourcesInCategory.length - 1) return;

    const nextResource = resourcesInCategory[currentIndex + 1];
    
    try {
      await updateDoc(doc(db, 'resources', resource.id), { order: nextResource.order });
      await updateDoc(doc(db, 'resources', nextResource.id), { order: resource.order });
    } catch (error) {
      console.error('Error reordering resource:', error);
      alert('Failed to reorder resource. Please try again.');
    }
  };

  return {
    // State
    resources,
    resourceCategories,
    newResource,
    setNewResource,
    editingResourceId,
    editResourceForm,
    setEditResourceForm,
    newCategory,
    setNewCategory,
    editingCategoryId,
    editCategoryName,
    setEditCategoryName,
    // Category handlers
    handleAddCategory,
    handleEditCategory,
    handleSaveCategory,
    handleCancelEditCategory,
    handleDeleteCategory,
    handleMoveCategoryUp,
    handleMoveCategoryDown,
    // Resource handlers
    handleAddResource,
    handleEditResource,
    handleSaveResource,
    handleCancelEditResource,
    handleDeleteResource,
    handleMoveResourceUp,
    handleMoveResourceDown
  };
};

/**
 * Student Resources Landing Page Component
 */
export const ResourcesLandingPage = ({ resources, resourceCategories, words = [], quotes = [] }) => {
  // Group resources by category
  const categorizedResources = resourceCategories.map(category => ({
    category,
    resources: resources.filter(r => r.categoryId === category.id)
  })).filter(group => group.resources.length > 0);

  const uncategorizedResources = resources.filter(r => !r.categoryId);

  return (
    <div className="resources-container">
      <h2 className="resources-title">Resources</h2>
      <p className="resources-description">
        Helpful links and materials for your learning journey
      </p>

      {/* Word and Quote of the Day */}
      {(words.length > 0 || quotes.length > 0) && (
        <div className="word-quote-container">
          {words.length > 0 && <WordOfDayDisplay words={words} />}
          {quotes.length > 0 && <QuoteOfDayDisplay quotes={quotes} />}
        </div>
      )}

      {/* Categorized Resources */}
      {categorizedResources.map(({ category, resources: categoryResources }) => (
        <div key={category.id} className="resource-category-section">
          <h3 className="resource-category-title">{category.name}</h3>
          <div className="resources-grid">
            {categoryResources.map((resource) => (
              <a
                key={resource.id}
                href={ensureUrlProtocol(resource.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="resource-card"
              >
                <h4 className="resource-title">{resource.title}</h4>
                {resource.description && (
                  <p className="resource-description">{resource.description}</p>
                )}
                <i className="fa-solid fa-arrow-up-right-from-square resource-link-indicator"></i>
              </a>
            ))}
          </div>
        </div>
      ))}

      {/* Uncategorized Resources */}
      {uncategorizedResources.length > 0 && (
        <div className="resource-category-section">
          {categorizedResources.length > 0 && (
            <h3 className="resource-category-title">Other Resources</h3>
          )}
          <div className="resources-grid">
            {uncategorizedResources.map((resource) => (
              <a
                key={resource.id}
                href={ensureUrlProtocol(resource.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="resource-card"
              >
                <h4 className="resource-title">{resource.title}</h4>
                {resource.description && (
                  <p className="resource-description">{resource.description}</p>
                )}
                <i className="fa-solid fa-arrow-up-right-from-square resource-link-indicator"></i>
              </a>
            ))}
          </div>
        </div>
      )}

      {resources.length === 0 && (
        <p className="no-posts">No resources available yet.</p>
      )}
    </div>
  );
};

/**
 * Teacher Resource Management View Component
 */
export const ResourceManagementView = ({
  resources,
  resourceCategories,
  newResource,
  setNewResource,
  editingResourceId,
  editResourceForm,
  setEditResourceForm,
  newCategory,
  setNewCategory,
  editingCategoryId,
  editCategoryName,
  setEditCategoryName,
  handleAddCategory,
  handleEditCategory,
  handleSaveCategory,
  handleCancelEditCategory,
  handleDeleteCategory,
  handleMoveCategoryUp,
  handleMoveCategoryDown,
  handleAddResource,
  handleEditResource,
  handleSaveResource,
  handleCancelEditResource,
  handleDeleteResource,
  handleMoveResourceUp,
  handleMoveResourceDown
}) => {
  return (
    <div className="post">
      <h2>Resource Management</h2>
      <p className="transcript-description">
        Add and organize learning resources for students
      </p>

      {/* Add New Resource Section */}
      <div className="add-tag-section">
        <h3>Add New Resource</h3>
        <div className="form-group">
          <label>Title:</label>
          <input
            type="text"
            value={newResource.title}
            onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
            placeholder="Resource title"
            className="post-title-input"
          />
        </div>
        <div className="form-group">
          <label>URL:</label>
          <input
            type="text"
            value={newResource.url}
            onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
            placeholder="https://example.com"
            className="post-title-input"
          />
        </div>
        <div className="form-group">
          <label>Description (optional):</label>
          <textarea
            value={newResource.description}
            onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
            placeholder="Brief description of the resource"
            className="post-description-input"
            rows="2"
          />
        </div>
        <div className="form-group">
          <label>Category (optional):</label>
          <select
            value={newResource.categoryId}
            onChange={(e) => setNewResource({ ...newResource, categoryId: e.target.value })}
            className="project-type-dropdown"
          >
            <option value="">Uncategorized</option>
            {resourceCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <button onClick={handleAddResource} className="class-assignment-button">
          Add Resource
        </button>
      </div>

      {/* Category Management Section */}
      <div className="resource-category-management-section">
        <h3>Categories</h3>
        <div className="add-tag-section">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New category name"
            className="post-title-input"
          />
          <button onClick={handleAddCategory} className="class-assignment-button">
            Add Category
          </button>
        </div>

        <div className="category-list">
          {resourceCategories.map((category, index) => (
            <div key={category.id} className="category-item">
              {editingCategoryId === category.id ? (
                <div className="category-edit-form">
                  <input
                    type="text"
                    value={editCategoryName}
                    onChange={(e) => setEditCategoryName(e.target.value)}
                    className="post-title-input"
                  />
                  <div className="category-actions">
                    <button onClick={handleSaveCategory} className="save-btn">
                      Save
                    </button>
                    <button onClick={handleCancelEditCategory} className="cancel-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="category-info">
                    <strong className="category-name">{category.name}</strong>
                    <span className="category-count">
                      ({resources.filter(r => r.categoryId === category.id).length} resources)
                    </span>
                  </div>
                  <div className="category-actions">
                    <button
                      onClick={() => handleMoveCategoryUp(category)}
                      disabled={index === 0}
                      className="reorder-btn"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveCategoryDown(category)}
                      disabled={index === resourceCategories.length - 1}
                      className="reorder-btn"
                    >
                      ↓
                    </button>
                    <button onClick={() => handleEditCategory(category)} className="edit-btn">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteCategory(category.id)} className="delete-btn">
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Resources List by Category */}
      <div className="resources-management-list">
        <h3>Manage Resources</h3>
        
        {/* Resources by Category */}
        {resourceCategories.map((category) => {
          const categoryResources = resources
            .filter(r => r.categoryId === category.id)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

          if (categoryResources.length === 0) return null;

          return (
            <div key={category.id} className="resource-category-management-section">
              <h4 className="resource-category-title">{category.name}</h4>
              {categoryResources.map((resource, index) => (
                <div key={resource.id} className="resource-management-item">
                  {editingResourceId === resource.id ? (
                    <div className="resource-edit-form">
                      <div className="form-group">
                        <label>Title:</label>
                        <input
                          type="text"
                          value={editResourceForm.title}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, title: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>URL:</label>
                        <input
                          type="text"
                          value={editResourceForm.url}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, url: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description:</label>
                        <textarea
                          value={editResourceForm.description}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, description: e.target.value })}
                          className="post-description-input"
                          rows="2"
                        />
                      </div>
                      <div className="form-group">
                        <label>Category:</label>
                        <select
                          value={editResourceForm.categoryId}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, categoryId: e.target.value })}
                          className="project-type-dropdown"
                        >
                          <option value="">Uncategorized</option>
                          {resourceCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="resource-edit-actions">
                        <button onClick={handleSaveResource} className="save-btn">
                          Save
                        </button>
                        <button onClick={handleCancelEditResource} className="cancel-btn">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="resource-info">
                        <h5 className="resource-management-title">{resource.title}</h5>
                        <p className="resource-management-url">
                          <a href={ensureUrlProtocol(resource.url)} target="_blank" rel="noopener noreferrer">
                            {resource.url}
                          </a>
                        </p>
                        {resource.description && (
                          <p className="resource-management-description">{resource.description}</p>
                        )}
                      </div>
                      <div className="resource-actions">
                        <button
                          onClick={() => handleMoveResourceUp(resource)}
                          disabled={index === 0}
                          className="reorder-btn"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleMoveResourceDown(resource)}
                          disabled={index === categoryResources.length - 1}
                          className="reorder-btn"
                        >
                          ↓
                        </button>
                        <button onClick={() => handleEditResource(resource)} className="edit-btn">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteResource(resource.id)} className="delete-btn">
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {/* Uncategorized Resources */}
        {(() => {
          const uncategorizedResources = resources
            .filter(r => !r.categoryId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

          if (uncategorizedResources.length === 0) return null;

          return (
            <div className="resource-category-management-section">
              <h4 className="resource-category-title">Uncategorized</h4>
              {uncategorizedResources.map((resource, index) => (
                <div key={resource.id} className="resource-management-item">
                  {editingResourceId === resource.id ? (
                    <div className="resource-edit-form">
                      <div className="form-group">
                        <label>Title:</label>
                        <input
                          type="text"
                          value={editResourceForm.title}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, title: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>URL:</label>
                        <input
                          type="text"
                          value={editResourceForm.url}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, url: e.target.value })}
                          className="post-title-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description:</label>
                        <textarea
                          value={editResourceForm.description}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, description: e.target.value })}
                          className="post-description-input"
                          rows="2"
                        />
                      </div>
                      <div className="form-group">
                        <label>Category:</label>
                        <select
                          value={editResourceForm.categoryId}
                          onChange={(e) => setEditResourceForm({ ...editResourceForm, categoryId: e.target.value })}
                          className="project-type-dropdown"
                        >
                          <option value="">Uncategorized</option>
                          {resourceCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="resource-edit-actions">
                        <button onClick={handleSaveResource} className="save-btn">
                          Save
                        </button>
                        <button onClick={handleCancelEditResource} className="cancel-btn">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="resource-info">
                        <h5 className="resource-management-title">{resource.title}</h5>
                        <p className="resource-management-url">
                          <a href={ensureUrlProtocol(resource.url)} target="_blank" rel="noopener noreferrer">
                            {resource.url}
                          </a>
                        </p>
                        {resource.description && (
                          <p className="resource-management-description">{resource.description}</p>
                        )}
                      </div>
                      <div className="resource-actions">
                        <button
                          onClick={() => handleMoveResourceUp(resource)}
                          disabled={index === 0}
                          className="reorder-btn"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleMoveResourceDown(resource)}
                          disabled={index === uncategorizedResources.length - 1}
                          className="reorder-btn"
                        >
                          ↓
                        </button>
                        <button onClick={() => handleEditResource(resource)} className="edit-btn">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteResource(resource.id)} className="delete-btn">
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {resources.length === 0 && (
          <p className="no-posts">No resources added yet.</p>
        )}
      </div>
    </div>
  );
};

const resourceComponents = {
  useResourceManagement,
  ResourcesLandingPage,
  ResourceManagementView
};

export default resourceComponents;

